import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode, useEffect } from 'react';
import type { SimulationResult, BottleneckAlert } from '../utils/workloadSimulation';

export interface KaizenOverride {
  fte?: number;
  rework?: number;
  gainPercent?: number;
  targetMetric?: 'cycleTime' | 'leadTime';
  // Compatibilidade com payloads anteriores
  extraFTE?: number;
  reworkReduction?: number;
  timeOverride?: number;
}

export type SimulationHorizon = 'daily' | 'weekly' | 'monthly';
export type SimulationShiftPreset = '1_shift' | '2_shifts' | 'plus_2h';

export interface DecisionMixOverride {
  simPct: number;
  naoPct: number;
}

export interface SimulationBaseline {
  leadTimeDays: number;
  costOfInertia: number;
}

export interface SimulationSnapshot {
  id: string;
  name: string;
  createdAt: string;
  demand: number;
  horizon: SimulationHorizon;
  shiftPreset: SimulationShiftPreset;
  batchSize: number;
  kaizenOverrides: Record<string, KaizenOverride>;
  decisionMixOverrides: Record<string, DecisionMixOverride>;
  leadTimeDays: number;
  costOfInertia: number;
}

export interface SimulationScenario {
  id: string;
  name: string;
  createdAt: string;
  demand: number;
  kaizenOverrides: Record<string, KaizenOverride>;
  horizon: SimulationHorizon;
  shift: SimulationShiftPreset;
  batchSize: number;
  totalLeadTime: number;
  inertiaCost: number;
  leadTimeGainPercent?: number;
  isMilestone?: boolean;
  source?: 'manual' | 'insight_modal' | 'optimization' | 'unknown';
  kpis?: {
    leadTimeMinutes: number;
    pcePercent: number;
    nvaCost: number;
    deliveryCapacityPerDay: number;
  };
  nodeSnapshot?: Array<{
    id: string;
    name: string;
    traMinutes: number;
    tpMinutes: number;
    baseFte: number;
    extraFte: number;
    totalFte: number;
  }>;
}

export interface SaveScenarioOptions {
  totalLeadTime?: number;
  inertiaCost?: number;
  leadTimeGainPercent?: number;
  isMilestone?: boolean;
  source?: 'manual' | 'insight_modal' | 'optimization' | 'unknown';
  kpis?: SimulationScenario['kpis'];
  nodeSnapshot?: SimulationScenario['nodeSnapshot'];
}

interface SimulationContextType {
  // API nova (estado global solicitado)
  isActive: boolean;
  isSimulationActive: boolean;
  setIsActive: (value: boolean) => void;
  setIsSimulationActive: (value: boolean) => void;
  demand: number;
  setDemand: (value: number) => void;
  kaizenOverrides: Record<string, KaizenOverride>;
  setKaizenOverride: (nodeId: string, override: KaizenOverride) => void;
  setKaizenOverrides: (overrides: Record<string, KaizenOverride>) => void;
  clearKaizenOverrides: () => void;
  shiftPreset: SimulationShiftPreset;
  setShiftPreset: (value: SimulationShiftPreset) => void;
  batchSize: number;
  setBatchSize: (value: number) => void;
  decisionMixOverrides: Record<string, DecisionMixOverride>;
  setDecisionMixOverride: (nodeId: string, override: DecisionMixOverride) => void;
  clearDecisionMixOverrides: () => void;
  baseline: SimulationBaseline | null;
  setBaseline: (baseline: SimulationBaseline | null) => void;
  snapshots: SimulationSnapshot[];
  saveSnapshot: (snapshot: Omit<SimulationSnapshot, 'id' | 'createdAt'> & { name?: string }) => string;
  applySnapshot: (snapshotId: string) => void;
  deleteSnapshot: (snapshotId: string) => void;
  clearSnapshots: () => void;
  savedScenarios: SimulationScenario[];
  saveScenario: (name?: string, options?: SaveScenarioOptions) => string | null;
  saveCurrentScenario: (name?: string, options?: SaveScenarioOptions) => string | null;
  loadScenario: (id: string) => void;
  deleteScenario: (id: string) => void;
  baselineCost: number | null;
  baselineLeadTime: number | null;
  ensureBaseline: (leadTime: number, inertiaCost: number) => void;
  setCurrentKpis: (leadTime: number, inertiaCost: number) => void;

  // API legado (compatibilidade)
  isSimulationMode: boolean;
  setIsSimulationMode: (value: boolean) => void;
  simulatedDemand: number;
  setSimulatedDemand: (value: number) => void;
  horizon: SimulationHorizon;
  setHorizon: (horizon: SimulationHorizon) => void;
  simulationResult: SimulationResult | null;
  setSimulationResult: (result: SimulationResult | null) => void;
  overloadedCardIds: Set<string>;
  setOverloadedCardIds: (ids: Set<string>) => void;
  bottleneckAlerts: BottleneckAlert[];
  setBottleneckAlerts: (alerts: BottleneckAlert[]) => void;
  calculateNodeLoad: (nodeId: string, baseCycleTime: number) => number;
  getNodeOccupancy: (nodeId: string) => number | null;
}

const SimulationContext = createContext<SimulationContextType | undefined>(undefined);
const SCENARIOS_STORAGE_KEY = 'workflow_manager_simulation_scenarios_v2';

interface SimulationProviderProps {
  children: ReactNode;
}

const loadStoredScenarios = (): SimulationScenario[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(SCENARIOS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item) => item && typeof item.id === 'string' && typeof item.name === 'string');
  } catch {
    return [];
  }
};

const normalizeOverride = (override: KaizenOverride): KaizenOverride => {
  const fte = override.fte ?? override.extraFTE;
  const rework = override.rework ?? override.reworkReduction;
  return {
    ...override,
    fte,
    rework,
    gainPercent: Math.max(0, Math.min(100, Number(override.gainPercent ?? rework ?? 0))),
    targetMetric: override.targetMetric ?? 'cycleTime',
    extraFTE: fte,
    reworkReduction: rework,
  };
};

export const SimulationProvider: React.FC<SimulationProviderProps> = ({ children }) => {
  const [isActive, setIsActiveState] = useState(false);
  const [demand, setDemand] = useState(0);
  const [horizon, setHorizon] = useState<SimulationHorizon>('daily');
  const [kaizenOverrides, setKaizenOverrides] = useState<Record<string, KaizenOverride>>({});
  const [shiftPreset, setShiftPreset] = useState<SimulationShiftPreset>('1_shift');
  const [batchSize, setBatchSize] = useState(1);
  const [decisionMixOverrides, setDecisionMixOverrides] = useState<Record<string, DecisionMixOverride>>({});
  const [baseline, setBaseline] = useState<SimulationBaseline | null>(null);
  const [snapshots, setSnapshots] = useState<SimulationSnapshot[]>([]);
  const [savedScenarios, setSavedScenarios] = useState<SimulationScenario[]>(() => loadStoredScenarios());
  const [baselineCost, setBaselineCost] = useState<number | null>(null);
  const [baselineLeadTime, setBaselineLeadTime] = useState<number | null>(null);
  const [currentLeadTime, setCurrentLeadTime] = useState(0);
  const [currentInertiaCost, setCurrentInertiaCost] = useState(0);
  const [simulationResult, setSimulationResult] = useState<SimulationResult | null>(null);
  const [overloadedCardIds, setOverloadedCardIds] = useState<Set<string>>(new Set());
  const [bottleneckAlerts, setBottleneckAlerts] = useState<BottleneckAlert[]>([]);
  const setIsActive = useCallback((value: boolean) => {
    setIsActiveState((prev) => {
      if (value && !prev) {
        // Nova simulação: baseline será travado no primeiro KPI calculado da sessão
        setBaselineCost(null);
        setBaselineLeadTime(null);
      }
      if (!value && prev) {
        setBaseline(null);
        setSnapshots([]);
        setKaizenOverrides({});
        setDecisionMixOverrides({});
        setOverloadedCardIds(new Set());
        setBottleneckAlerts([]);
        setBaselineCost(null);
        setBaselineLeadTime(null);
        setCurrentLeadTime(0);
        setCurrentInertiaCost(0);
      }
      return value;
    });
  }, []);

  const setKaizenOverride = useCallback((nodeId: string, override: KaizenOverride) => {
    setKaizenOverrides((prev) => {
      const current = prev[nodeId] ?? {};
      const merged = normalizeOverride({ ...current, ...override });
      return { ...prev, [nodeId]: merged };
    });
  }, []);

  const replaceKaizenOverrides = useCallback((overrides: Record<string, KaizenOverride>) => {
    const normalized: Record<string, KaizenOverride> = {};
    Object.entries(overrides ?? {}).forEach(([nodeId, override]) => {
      normalized[nodeId] = normalizeOverride(override ?? {});
    });
    setKaizenOverrides(normalized);
  }, []);

  const clearKaizenOverrides = useCallback(() => {
    setKaizenOverrides({});
  }, []);

  const setDecisionMixOverride = useCallback((nodeId: string, override: DecisionMixOverride) => {
    const simPct = Math.max(0, Math.min(100, Number(override.simPct ?? 50)));
    const naoPct = Math.max(0, Math.min(100, Number(override.naoPct ?? (100 - simPct))));
    const normalized = simPct + naoPct === 100
      ? { simPct, naoPct }
      : { simPct, naoPct: Math.max(0, 100 - simPct) };

    setDecisionMixOverrides((prev) => ({ ...prev, [nodeId]: normalized }));
  }, []);

  const clearDecisionMixOverrides = useCallback(() => {
    setDecisionMixOverrides({});
  }, []);

  const setIsSimulationMode = useCallback((value: boolean) => {
    setIsActive(value);
  }, []);

  const setSimulatedDemand = useCallback((value: number) => {
    setDemand(value);
  }, []);

  const saveSnapshot = useCallback(
    (snapshot: Omit<SimulationSnapshot, 'id' | 'createdAt'> & { name?: string }) => {
      const id = `scenario-${Date.now()}`;
      const createdAt = new Date().toISOString();
      const name = snapshot.name?.trim() || `Cenário ${snapshots.length + 1}`;
      const payload: SimulationSnapshot = {
        id,
        name,
        createdAt,
        demand: snapshot.demand,
        horizon: snapshot.horizon,
        shiftPreset: snapshot.shiftPreset,
        batchSize: snapshot.batchSize,
        kaizenOverrides: { ...snapshot.kaizenOverrides },
        decisionMixOverrides: { ...snapshot.decisionMixOverrides },
        leadTimeDays: snapshot.leadTimeDays,
        costOfInertia: snapshot.costOfInertia,
      };
      setSnapshots((prev) => [payload, ...prev]);
      return id;
    },
    [snapshots.length],
  );

  const applySnapshot = useCallback((snapshotId: string) => {
    setSnapshots((prev) => {
      const target = prev.find((item) => item.id === snapshotId);
      if (!target) return prev;
      setDemand(target.demand);
      setHorizon(target.horizon);
      setShiftPreset(target.shiftPreset);
      setBatchSize(target.batchSize);
      setKaizenOverrides({ ...target.kaizenOverrides });
      setDecisionMixOverrides({ ...target.decisionMixOverrides });
      return prev;
    });
  }, []);

  const deleteSnapshot = useCallback((snapshotId: string) => {
    setSnapshots((prev) => prev.filter((item) => item.id !== snapshotId));
  }, []);

  const clearSnapshots = useCallback(() => {
    setSnapshots([]);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(SCENARIOS_STORAGE_KEY, JSON.stringify(savedScenarios));
    } catch {
      // persistência local best-effort
    }
  }, [savedScenarios]);

  const setCurrentKpis = useCallback((leadTime: number, inertiaCost: number) => {
    const safeLeadTime = Number.isFinite(leadTime) ? leadTime : 0;
    const safeInertiaCost = Number.isFinite(inertiaCost) ? inertiaCost : 0;
    setCurrentLeadTime(safeLeadTime);
    setCurrentInertiaCost(safeInertiaCost);

    // Trava baseline automaticamente na primeira leitura de KPI após ativar simulação
    if (isActive && baselineLeadTime === null) setBaselineLeadTime(safeLeadTime);
    if (isActive && baselineCost === null) setBaselineCost(safeInertiaCost);
  }, [baselineCost, baselineLeadTime, isActive]);

  const ensureBaseline = useCallback((leadTime: number, inertiaCost: number) => {
    if (baselineLeadTime === null) {
      setBaselineLeadTime(Number.isFinite(leadTime) ? leadTime : 0);
    }
    if (baselineCost === null) {
      setBaselineCost(Number.isFinite(inertiaCost) ? inertiaCost : 0);
    }
  }, [baselineCost, baselineLeadTime]);

  const saveScenario = useCallback((name?: string, options?: SaveScenarioOptions) => {
    if (!isActive) return null;
    const scenarioId = `scenario-${Date.now()}`;
    const scenarioName = name?.trim() || `${options?.isMilestone ? 'Marco' : 'Cenário'} ${savedScenarios.length + 1}`;
    const scenario: SimulationScenario = {
      id: scenarioId,
      name: scenarioName,
      createdAt: new Date().toISOString(),
      demand,
      kaizenOverrides: { ...kaizenOverrides },
      horizon,
      shift: shiftPreset,
      batchSize,
      totalLeadTime: Number.isFinite(options?.totalLeadTime as number) ? Number(options?.totalLeadTime) : currentLeadTime,
      inertiaCost: Number.isFinite(options?.inertiaCost as number) ? Number(options?.inertiaCost) : currentInertiaCost,
      leadTimeGainPercent: Number.isFinite(options?.leadTimeGainPercent as number)
        ? Number(options?.leadTimeGainPercent)
        : undefined,
      isMilestone: Boolean(options?.isMilestone),
      source: options?.source ?? 'manual',
      kpis: options?.kpis,
      nodeSnapshot: options?.nodeSnapshot,
    };
    setSavedScenarios((prev) => [scenario, ...prev]);
    return scenarioId;
  }, [
    isActive,
    savedScenarios.length,
    demand,
    kaizenOverrides,
    horizon,
    shiftPreset,
    batchSize,
    currentLeadTime,
    currentInertiaCost,
  ]);

  const saveCurrentScenario = useCallback((name?: string, options?: SaveScenarioOptions) => {
    return saveScenario(name, { ...options, source: options?.source ?? 'manual' });
  }, [saveScenario]);

  const loadScenario = useCallback((id: string) => {
    const scenario = savedScenarios.find((item) => item.id === id);
    if (!scenario) return;
    setDemand(scenario.demand);
    setHorizon(scenario.horizon);
    setShiftPreset(scenario.shift);
    setBatchSize(scenario.batchSize);
    setKaizenOverrides({ ...scenario.kaizenOverrides });
  }, [savedScenarios]);

  const deleteScenario = useCallback((id: string) => {
    setSavedScenarios((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const calculateNodeLoad = useCallback((nodeId: string, baseCycleTime: number): number => {
    if (!simulationResult) return 0;

    const department = simulationResult.departmentMetrics.find((metric) =>
      metric.assignedCards.some((card) => card.cardId === nodeId),
    );
    if (!department) return 0;

    const override = kaizenOverrides[nodeId];
    const reworkReduction = Math.max(0, Math.min(100, Number(override?.rework ?? 0)));
    const extraFte = Math.max(0, Number(override?.fte ?? 0));
    const adjustedCycleTime = (override?.timeOverride ?? baseCycleTime) * (1 - reworkReduction / 100);
    const workload = demand * adjustedCycleTime;

    const adjustedHeadcount = department.headcount + extraFte;
    const capacityPerPerson = department.totalCapacity / Math.max(department.headcount, 1);
    const adjustedCapacity =
      capacityPerPerson * adjustedHeadcount *
      (department.effectiveCapacity / Math.max(department.totalCapacity, 1));

    if (adjustedCapacity <= 0) return 999;
    return Math.round((workload / adjustedCapacity) * 100);
  }, [simulationResult, kaizenOverrides, demand]);

  const getNodeOccupancy = useCallback((nodeId: string): number | null => {
    if (!isActive || !simulationResult) return null;

    const department = simulationResult.departmentMetrics.find((metric) =>
      metric.assignedCards.some((card) => card.cardId === nodeId),
    );
    if (!department) return null;

    return Math.round(department.occupancyRate);
  }, [isActive, simulationResult]);

  const value = useMemo<SimulationContextType>(() => ({
    // API nova
    isActive,
    isSimulationActive: isActive,
    setIsActive,
    setIsSimulationActive: setIsActive,
    demand,
    setDemand,
    kaizenOverrides,
    setKaizenOverride,
    setKaizenOverrides: replaceKaizenOverrides,
    clearKaizenOverrides,
    shiftPreset,
    setShiftPreset,
    batchSize,
    setBatchSize,
    decisionMixOverrides,
    setDecisionMixOverride,
    clearDecisionMixOverrides,
    baseline,
    setBaseline,
    snapshots,
    saveSnapshot,
    applySnapshot,
    deleteSnapshot,
    clearSnapshots,
    savedScenarios,
    saveScenario,
    saveCurrentScenario,
    loadScenario,
    deleteScenario,
    baselineCost,
    baselineLeadTime,
    ensureBaseline,
    setCurrentKpis,

    // API legado
    isSimulationMode: isActive,
    setIsSimulationMode,
    simulatedDemand: demand,
    setSimulatedDemand,
    horizon,
    setHorizon,
    simulationResult,
    setSimulationResult,
    overloadedCardIds,
    setOverloadedCardIds,
    bottleneckAlerts,
    setBottleneckAlerts,
    calculateNodeLoad,
    getNodeOccupancy,
  }), [
    isActive,
    demand,
    kaizenOverrides,
    setKaizenOverride,
    replaceKaizenOverrides,
    clearKaizenOverrides,
    shiftPreset,
    batchSize,
    decisionMixOverrides,
    setDecisionMixOverride,
    clearDecisionMixOverrides,
    baseline,
    snapshots,
    saveSnapshot,
    applySnapshot,
    deleteSnapshot,
    clearSnapshots,
    savedScenarios,
    saveScenario,
    saveCurrentScenario,
    loadScenario,
    deleteScenario,
    baselineCost,
    baselineLeadTime,
    ensureBaseline,
    setCurrentKpis,
    setIsSimulationMode,
    setSimulatedDemand,
    horizon,
    simulationResult,
    overloadedCardIds,
    bottleneckAlerts,
    calculateNodeLoad,
    getNodeOccupancy,
  ]);

  return (
    <SimulationContext.Provider value={value}>
      {children}
    </SimulationContext.Provider>
  );
};

export const useSimulation = (): SimulationContextType => {
  const context = useContext(SimulationContext);
  if (!context) {
    throw new Error('useSimulation must be used within SimulationProvider');
  }
  return context;
};

export const useSimulationSafe = (): SimulationContextType | null => {
  return useContext(SimulationContext) ?? null;
};
